import React, { useState, useEffect } from 'react';
import sportsService from '../services/sportsService';
import LoadingSpinner from './LoadingSpinner';
import './SportAdmin.css';

const SportAdmin = () => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingSport, setEditingSport] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    icon: '',
    description: ''
  });

  // Load sports on component mount
  useEffect(() => {
    loadSports();
  }, []);

  const loadSports = async () => {
    try {
      setLoading(true);
      setError(null);
      const sportsData = await sportsService.getSports();
      setSports(sportsData);
    } catch (err) {
      setError('Error al cargar los deportes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingSport) {
        await sportsService.updateSport(editingSport.id, formData);
      } else {
        await sportsService.createSport(formData);
      }
      
      await loadSports();
      resetForm();
    } catch (err) {
      setError('Error al guardar el deporte: ' + err.message);
    }
  };

  const handleEdit = (sport) => {
    setEditingSport(sport);
    setFormData({
      name: sport.name || '',
      category: sport.category || '',
      icon: sport.icon || '',
      description: sport.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (sport) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el deporte "${sport.name}"?`)) {
      try {
        setError(null);
        await sportsService.deleteSport(sport.id);
        await loadSports();
      } catch (err) {
        setError('Error al eliminar el deporte: ' + err.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      icon: '',
      description: ''
    });
    setEditingSport(null);
    setShowForm(false);
  };

  const handleCancel = () => {
    resetForm();
  };

  if (loading) {
    return <LoadingSpinner message="Cargando deportes..." />;
  }

  return (
    <div className="sport-admin">
      <div className="sport-admin-header">
        <h2>Administración de Deportes</h2>
        <button 
          onClick={() => setShowForm(true)}
          className="btn btn-primary"
        >
          ➕ Nuevo Deporte
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showForm && (
        <div className="sport-form-modal">
          <div className="sport-form-container">
            <h3>{editingSport ? 'Editar Deporte' : 'Nuevo Deporte'}</h3>
            <form onSubmit={handleSubmit} className="sport-form">
              <div className="form-group">
                <label htmlFor="name">Nombre del Deporte</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Fútbol, Tenis, Natación"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Categoría</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar categoría</option>
                  <option value="deportes_acuaticos">Deportes Acuáticos</option>
                  <option value="deportes_terrestres">Deportes Terrestres</option>
                  <option value="deportes_aereos">Deportes Aéreos</option>
                  <option value="deportes_combate">Deportes de Combate</option>
                  <option value="deportes_equipo">Deportes de Equipo</option>
                  <option value="deportes_individuales">Deportes Individuales</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="icon">Icono (Emoji)</label>
                <input
                  type="text"
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  placeholder="Ej: ⚽, 🏀, 🏊‍♂️"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Descripción</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Descripción del deporte..."
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSport ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="sports-list">
        {sports.length === 0 ? (
          <div className="no-sports">
            <div className="no-sports-icon">🏃‍♂️</div>
            <h3>No hay deportes registrados</h3>
            <p>Comienza creando tu primer deporte.</p>
          </div>
        ) : (
          <div className="sports-grid">
            {sports.map(sport => (
              <div key={sport.id} className="sport-card">
                <div className="sport-header">
                  <div className="sport-icon">{sport.icon || '🏃‍♂️'}</div>
                  <h3>{sport.name}</h3>
                </div>
                <div className="sport-details">
                  <div className="sport-category">
                    <strong>Categoría:</strong> {sport.category}
                  </div>
                  {sport.description && (
                    <div className="sport-description">
                      <strong>Descripción:</strong> {sport.description}
                    </div>
                  )}
                </div>
                <div className="sport-actions">
                  <button 
                    onClick={() => handleEdit(sport)}
                    className="btn btn-secondary btn-sm"
                  >
                    ✏️ Editar
                  </button>
                  <button 
                    onClick={() => handleDelete(sport)}
                    className="btn btn-danger btn-sm"
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SportAdmin;
